import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import type { Company, User } from 'types';

import GoogleOneTap from 'components/googleOneTapConnect';
import StickyFooterContainer from 'components/stickyFooter/StickyFooterContainer';
import { useCtxHistory, useJobAlertTrigger, useTracking } from 'hooks';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { oneTapGoogleLogin } from 'services/loginService';
import { startPollingForPushSubscriptionChange } from 'services/pushNotificationsService';
import { sessionPopupService } from 'services/sessionPopupService';
import { updateUserInfo } from 'services/userProfileService';
import { trackUserPageViewActivities } from 'services/userService';
import {
  closeLocationInputModal,
  closePostSignUpPopup,
  dispatchOpenSessionTimerSignupModal,
  dispatchValidateSessionConditionalSignupModal,
  openSignUpModal,
} from 'store/modals/actions';
import { __updateuser, updateAnonymousUser } from 'store/user/actions';
import { getCookie } from 'utils';
import { gtmLogSignupEvent, logEventGTM } from 'utils/analyticsGTM';
import { PAGE_CATEGORY } from 'utils/constant';

import careerMapClasses from 'css/careerMapOverlay.module.css';

const ToastContainer = dynamic(() => import('react-toastify').then((mod) => mod.ToastContainer), {
  ssr: false,
});

/** Modal Components */
const DLP = dynamic(() => import('widgets/dlp'), { ssr: false });

const ShareJobDLPPopup = dynamic(() => import('widgets/shareJobDLPPopup'), { ssr: false });

const EasyApplyForm = dynamic(() => import('widgets/easyApplyForm'), { ssr: false });

const AdviceArticlePopup = dynamic(() => import('widgets/adviceArticle'));

const SessionTimerSignupPopup = dynamic(() => import('widgets/sessionTimerSignupPopup'));

const SessionConditionalSignupPopup = dynamic(
  () => import('widgets/sessionConditionalSignupPopup')
);

const EmbedWidgetPopup = dynamic(() => import('widgets/embedWidgetPopup'));

const CollegePopup = dynamic(() => import('widgets/collegePopup'), { ssr: false });

const MoreInfoConfirmDialog = dynamic(() => import('widgets/moreInfoConfirmDialog'));

const RecommendedJobBubble = dynamic(() => import('components/popups/recommendedJobBubble'), {
  ssr: false,
});

const LoginPopup = dynamic(() => import('components/loginPopup'), {
  ssr: false,
});

const SurveyPopup = dynamic(() => import('components/surveyPopup/SurveyPopup'), { ssr: false });

const LocationInputFieldPopup = dynamic(
  () => import('components/jobs/card/LocationInputFieldPopup'),
  { ssr: false }
);

const PostSignUpFormPopup = dynamic(() => import('components/postSignUpFormPopup'), { ssr: false });

const SignupRecaptchaModal = dynamic(() => import('widgets/recaptcha/signupRecaptchaModal'), {
  ssr: false,
});

const PostApplyUXModal = dynamic(() => import('widgets/dlp/detail/postApplyUX'), { ssr: false });

const GatedSignupPopup = dynamic(() => import('widgets/gatedSignupPopup'), {
  ssr: false,
});

const NativeNotificationPrompt = dynamic(() => import('components/nativeNotificationPrompt'), {
  ssr: false,
});

const RBTemplatesModal = dynamic(() => import('components/resumeBuilder/popup/templates'), {
  ssr: false,
});

const RBDeletePopup = dynamic(() => import('components/resumeBuilder/popup/delete'), {
  ssr: false,
});

const RBSubscriptionModal = dynamic(() => import('components/resumeBuilder/popup/subscription'), {
  ssr: false,
});

const ResetPasswordModal = dynamic(() => import('components/resumeBuilder/popup/resetPassword'), {
  ssr: false,
});

const ResumePreviewModal = dynamic(() => import('components/resumeBuilder/preview/modal'), {
  ssr: false,
});

const JobAlertPopup = dynamic(() => import('components/jobAlertPopup'), { ssr: false });

const RBFormatDrawer = dynamic(() => import('components/resumeBuilder/popup/rbFormatDrawer'), {
  ssr: false,
});

const ChatBot = dynamic(() => import('components/popups/chatbotBubble'), { ssr: false });

const ExitIntentPopup = dynamic(() => import('components/exitIntentPopup'), { ssr: false });

const TrackingPixels = dynamic(
  () => import('components/trackingPixels').then((mod) => mod.TrackingPixels),
  { ssr: false }
);

interface IProps {
  user?: User;
  companyInfo?: Company;
  averageClickProb?: number;
  showSponsoredJobs?: boolean;
  showEmployerPopout?: boolean;
  isCareerSkillsPage?: boolean;
  isCareerWhatTheyDoPage?: boolean;
  isCareerEducationPage?: boolean;
  isCompanyLocationsPage?: boolean;
  isCareerDescriptionPage?: boolean;
  isCareerHowToHirePage?: boolean;
  hidePersonalizedJobs?: boolean;
}

export const ModalContainerPropFields = [
  'user',
  'companyInfo',
  'averageClickProb',
  'showSponsoredJobs',
  'showEmployerPopout',
  'isCareerSkillsPage',
  'isCareerWhatTheyDoPage',
  'isCareerEducationPage',
  'isCompanyLocationsPage',
  'isCareerDescriptionPage',
  'isCareerHowToHirePage',
  'hidePersonalizedJobs',
];

const ModalContainer = (props: IProps) => {
  const {
    averageClickProb,
    showSponsoredJobs,
    showEmployerPopout,
    isCareerSkillsPage,
    isCareerWhatTheyDoPage,
    isCareerEducationPage,
    isCompanyLocationsPage,
    isCareerDescriptionPage,
    isCareerHowToHirePage,
    hidePersonalizedJobs,
  } = props;

  const isChatbotPage = [
    isCareerSkillsPage,
    isCareerWhatTheyDoPage,
    isCareerEducationPage,
    isCompanyLocationsPage,
  ].some(Boolean);

  // Note: The HTTP_ACTIVITY will be logged in the page itself so
  // removing the additional logging here. If you want to remove the
  // HTTP_ACTIVITY being logged by default then add the page category in
  // the list below
  const logHTTPActivity = ![
    'Company Profile Settings',
    // Add more page category here if you want to remove HTTP_ACTIVITY being logged from here
  ].includes(window.pageCategory);

  const {
    modals,
    criteria,
    userGroup,
    isUserLoggedIn,
    previewModalInfo,
    googleOneTapClientId,
    enablePostSignUpSurvey,
    enableSignUpPopupForUserSession,
  } = useAppSelector((state) => ({
    modals: state.modals,
    criteria: state.locals.criteria,
    userGroup: state.locals.userGroup,
    isUserLoggedIn: state.user.isLoggedIn,
    previewModalInfo: state.resumeBuilder.previewModalInfo,
    googleOneTapClientId: state.locals.googleOneTapClientId,
    enablePostSignUpSurvey: state.locals.enablePostSignUpSurvey,
    enableSignUpPopupForUserSession: state.locals.enableSignUpPopupForUserSession,
  }));

  const isLocationInputPopupOpen = modals.locationInputPopup.open;

  const isPostSignUpPopupOpen = modals.postSignUpSurveyPopup.open;

  const locationInputPurpose = modals.locationInputPopup.purpose;

  const dispatch = useAppDispatch();

  const [showChatBot, setShowChatBot] = useState(false);

  const history = useCtxHistory();

  const tracking = useTracking();

  const jobAlertProps = useJobAlertTrigger({ criteria, companyInfo: props.companyInfo });

  const showSponsored = (() => {
    if (typeof showSponsoredJobs !== 'boolean' && typeof showEmployerPopout !== 'boolean') {
      return !!criteria?.title;
    }
    return !!showSponsoredJobs || !!showEmployerPopout;
  })();

  useEffect(() => {
    // Log HTTP_ACTIVITY
    const httpActivityPayload = {
      averageClickProb,
      pageJobTitle: criteria?.title || undefined,
      pageCity: criteria?.city || undefined,
      pageState: criteria?.state || undefined,
      pageCategory: window.pageCategory,
    };

    let zippiaUser = props.user;

    function setupSessionPopups() {
      if (!hidePersonalizedJobs) {
        sessionPopupService.handleSessionTimerPopup(
          zippiaUser,
          enableSignUpPopupForUserSession,
          modals.sessionTimerSignup,
          () => dispatch(dispatchOpenSessionTimerSignupModal())
        );
      }
      dispatch(dispatchValidateSessionConditionalSignupModal());
    }
    if (logHTTPActivity) {
      try {
        tracking.logActivity(httpActivityPayload).then(({ data }) => {
          const { user } = data;
          dispatch(updateAnonymousUser(user));
          zippiaUser = user;
          if (!window.userId) {
            window.userId = user.userId;
          }
          window.isLoggedIn = user.isLoggedIn || false;
          logEventGTM();
        });
      } catch (err) {}
    }
    setupSessionPopups();
    if (zippiaUser) startPollingForPushSubscriptionChange(zippiaUser);
    trackUserPageViewActivities(criteria);
  }, []);

  const onSignUpPopup = () => {
    const surveyStepSituation = getCookie('_zippia-surveyStepSituation');
    const survey = surveyStepSituation === 'groupB' ? '040' : '036';
    const query = {
      survey_step: 'step1',
      survey,
      utm_campaign: 'nav_sign_up',
    };
    dispatch(
      openSignUpModal({
        ...query,
        popupType: 'header-sign-up',
      })
    );
    history.pushQuery(query);
  };

  const handleCredentialResponse = async ({ credential }) => {
    try {
      const response = await oneTapGoogleLogin(credential);
      const { user } = response.data;
      const { isNewUser } = user;

      // Add location to user profile if this is a new user.
      if (isNewUser && (criteria.geoCity || criteria.geoState)) {
        const { geoCity = '', geoState = '', geoStateCode = '' } = criteria;
        const location = [
          {
            city: geoCity,
            state: geoCity ? geoStateCode : geoState,
          },
        ];
        await updateUserInfo({ location }, 'location');
      }

      dispatch(__updateuser(user));

      if (user && isNewUser && enablePostSignUpSurvey) {
        gtmLogSignupEvent('Google Identity');
        return;
      }
      window.location.reload();
    } catch (e) {
      console.error(e);
    }
  };

  const renderOneTapGoogleLogin = () => {
    const style = {
      position: 'fixed',
      top: '52px',
      right: '10px',
      zIndex: 999999,
      display: !modals.login.open ? 'block' : 'none',
    };
    return (
      <GoogleOneTap
        style={style}
        showDisplayOr={false}
        googleClientId={googleOneTapClientId}
        onGoogleOneTap={handleCredentialResponse}
      />
    );
  };

  const handleLocationInputPopupClose = () => {
    dispatch(closeLocationInputModal());
  };

  const handlePostSignUpPopupClose = () => {
    dispatch(closePostSignUpPopup());
    window.location.reload();
  };

  const getRecommendedBubbleSrc = () => {
    if (isCareerDescriptionPage) {
      return '#src=job-description-callout-emp4014-b30';
    }
    if (isCareerHowToHirePage) {
      return '#src=hth-callout-emp4014-b50';
    }

    return '';
  };

  const isSignProcessClosed =
    !modals.signUp?.open && !modals.gatedSignup?.open && !modals.login?.open;

  return (
    <>
      <LoginPopup openSignUpPopup={onSignUpPopup} />
      {/* signup modal popup */}
      {(modals.signUp.open || isLocationInputPopupOpen) &&
        !modals.signUp.surveyFullPage &&
        !modals.signUp.oneTapSignUp && (
          <div
            className={`${careerMapClasses['career-map-overlay']} ${
              modals.postApplyUX.open ? 'post-apply-ux-overlay' : ''
            }`}
          />
        )}
      <SurveyPopup />

      {!isUserLoggedIn && renderOneTapGoogleLogin()}

      {/* Location Input Popup will be displayed when user is not signed in
                and try to save/dismiss the job he/she is interested in */}
      <LocationInputFieldPopup
        purpose={locationInputPurpose}
        open={isLocationInputPopupOpen}
        onCloseModel={handleLocationInputPopupClose}
      />

      <PostSignUpFormPopup open={isPostSignUpPopupOpen} onCloseModel={handlePostSignUpPopupClose} />

      {/* DLP as popup, open only if dlp as isPopUp */}
      {modals.dlp.open && !(modals.login.open || modals.signUp.open) && modals.dlp.isPopUp && (
        <DLP />
      )}

      {modals.postApplyUX.open && <PostApplyUXModal />}

      {modals.share.open && modals.share.type === 'job' && <ShareJobDLPPopup />}

      {modals.easyApply.open && <EasyApplyForm />}

      {modals.adviceArticlePopup.articlePopupOpen && <AdviceArticlePopup />}

      {modals.sessionTimerSignup.open && isSignProcessClosed && <SessionTimerSignupPopup />}

      <NativeNotificationPrompt />

      {modals.sessionConditionalSignup.open && <SessionConditionalSignupPopup />}

      {modals.embedWidget.open && <EmbedWidgetPopup />}

      {modals.signupRecaptcha.open && <SignupRecaptchaModal />}

      {modals.collegePopup.open && <CollegePopup />}

      {modals.moreInfoModal.open && <MoreInfoConfirmDialog />}

      {modals.resumeBuilderModals.templates && <RBTemplatesModal />}

      {modals.resumeBuilderModals.delete && <RBDeletePopup />}

      {modals.resumeBuilderModals.subscription && <RBSubscriptionModal />}

      {modals.resumeBuilderModals.resetPassword && <ResetPasswordModal />}

      {!!previewModalInfo.open && <ResumePreviewModal />}

      {modals.jobAlert.open && <JobAlertPopup {...jobAlertProps} />}

      {window?.location?.pathname !== '/resume-builder/' && window?.userId && <GatedSignupPopup />}

      <ExitIntentPopup
        location={[
          {
            city: criteria?.geoCity || '',
            state: (criteria?.geoCity ? criteria?.geoStateCode : criteria?.geoState) || '',
          },
        ]}
      />

      {modals.rbFormatDrawer.open && <RBFormatDrawer />}

      {!!showSponsored && (
        <RecommendedJobBubble
          isEmployerPopout={showEmployerPopout}
          criteria={criteria}
          onNoJob={() => setShowChatBot(true)}
          src={getRecommendedBubbleSrc()}
        />
      )}

      {isChatbotPage && (showChatBot || !showSponsored) && <ChatBot jobTitle={criteria.title} />}

      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        closeButton={false}
      />

      <TrackingPixels />

      <StickyFooterContainer
        // Show the "Sticky Footer Container" only on Career Salary Page
        pageCategories={[PAGE_CATEGORY.careerSalaryPage]}
        globalTurnOn={userGroup?.['stickyFooterResumes'] === 'groupB'}
      />
    </>
  );
};

export default ModalContainer;
